import React, { useState } from 'react';
import { Building2, User, Hash, Code, Bell, Shield, Database, Archive, FileUp, Plus, Calendar, FolderOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocalAuth } from '../../hooks/useLocalAuth';
import { ProfileMenu } from '../dashboard/ProfileMenu';
import { CloudStorageConfig } from '../CloudStorageConfig';
import { ApiKeyManager } from '../ApiKeyManager';
import { WebhookManager } from '../WebhookManager';
import { ApiDocumentation } from '../ApiDocumentation';

interface NavigationButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  color: ButtonColor;
  to?: string;
}

type ButtonColor = 'blue' | 'green' | 'purple' | 'amber' | 'teal' | 'indigo' | 'emerald' | 'red';

const getButtonColors = (color: ButtonColor) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
    green: 'bg-green-100 text-green-700 hover:bg-green-200',
    purple: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
    amber: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
    teal: 'bg-teal-100 text-teal-700 hover:bg-teal-200',
    indigo: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
    emerald: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
    red: 'bg-red-100 text-red-700 hover:bg-red-200',
  };
  return colors[color];
};

const NavigationButton: React.FC<NavigationButtonProps> = ({ icon, label, onClick, disabled = false, color, to }) => {
  const buttonContent = (
    <>
      {icon}
      <span>{label}</span>
    </>
  );

  const className = `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto justify-center ${getButtonColors(color)}`;

  if (to) {
    return (
      <Link to={to} className={className}>
        {buttonContent}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      {buttonContent}
    </button>
  );
};

interface NavigationBarProps {
  showArchived: boolean;
  onToggleArchive: () => void;
  onShowTechnicianList: () => void;
  onShowKonzernIdManager: () => void;
  onShowApiMenu: () => void;
  onShowWebhookManager: () => void;
  onShowApiDocumentation: () => void;
  onShowSecurityMenu: () => void;
  onShowMigrationDialog: () => void;
  onShowImportDialog: () => void;
  onCreateBuilding: () => void;
}

export const NavigationBar: React.FC<NavigationBarProps> = ({
  showArchived,
  onToggleArchive,
  onShowTechnicianList,
  onShowKonzernIdManager,
  onShowApiMenu,
  onShowWebhookManager,
  onShowApiDocumentation,
  onShowSecurityMenu,
  onShowMigrationDialog,
  onShowImportDialog,
  onCreateBuilding,
}) => {
  const { user } = useLocalAuth();
  const [showCloudStorageConfig, setShowCloudStorageConfig] = useState(false);
  const [showApiKeyManager, setShowApiKeyManager] = useState(false);
  const [showWebhookManager, setShowWebhookManager] = useState(false);
  const [showApiDocumentation, setShowApiDocumentation] = useState(false);

  const handleApiMenuClick = () => {
    // Show API key manager by default
    setShowApiKeyManager(true);
  };

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top bar with logo and profile */}
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">InstallTrack Pro</h1>
          </div>
          
          {/* Profile Menu */}
          {user && <ProfileMenu user={user} />}
        </div>
        
        {/* Navigation bar */}
        <div className="flex flex-wrap items-center gap-2 py-3 border-t border-gray-200">
          <NavigationButton
            icon={<Archive className="w-4 h-4" />}
            label={showArchived ? 'Aktive anzeigen' : 'Archiv'}
            onClick={onToggleArchive}
            color="amber"
          />

          <NavigationButton
            icon={<Calendar className="w-4 h-4" />}
            label="Kalender"
            to="/calendar"
            color="blue"
          />

          {(!user?.permissions || user.permissions.canManageTechnicians) && (
            <NavigationButton
              icon={<User className="w-4 h-4" />}
              label="Techniker"
              onClick={onShowTechnicianList}
              color="blue"
            />
          )}

          {(!user?.permissions || user.permissions.canManageKonzernIds) && (
            <NavigationButton
              icon={<Hash className="w-4 h-4" />}
              label="Konzern IDs"
              onClick={onShowKonzernIdManager}
              color="purple"
            />
          )}

          <NavigationButton
            icon={<FolderOpen className="w-4 h-4" />}
            label="Cloud-Speicher"
            onClick={() => setShowCloudStorageConfig(true)}
            color="blue"
          />

          {(!user?.permissions || user.permissions.canAccessApi) && (
            <NavigationButton
              icon={<Code className="w-4 h-4" />}
              label="API"
              onClick={handleApiMenuClick}
              color="teal"
            />
          )}

          <NavigationButton
            icon={<Shield className="w-4 h-4" />}
            label="Sicherheit"
            onClick={onShowSecurityMenu}
            color="red"
          />

          <NavigationButton
            icon={<Database className="w-4 h-4" />}
            label="Datenbank"
            onClick={onShowMigrationDialog}
            color="indigo"
          />

          {!showArchived && (
            <>
              {(!user?.permissions || user.permissions.canImportData) && (
                <NavigationButton
                  icon={<FileUp className="w-4 h-4" />}
                  label="PDF importieren"
                  onClick={onShowImportDialog}
                  color="emerald"
                />
              )}
              
              {(!user?.permissions || user.permissions.canCreateBuildings) && (
                <NavigationButton
                  icon={<Plus className="w-4 h-4" />}
                  label="Neues Gebäude"
                  onClick={onCreateBuilding}
                  color="green"
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Cloud Storage Config Dialog */}
      {showCloudStorageConfig && (
        <CloudStorageConfig onClose={() => setShowCloudStorageConfig(false)} />
      )}

      {/* API Management Dialogs */}
      {showApiKeyManager && (
        <ApiKeyManager onClose={() => setShowApiKeyManager(false)} />
      )}

      {showWebhookManager && (
        <WebhookManager onClose={() => setShowWebhookManager(false)} />
      )}

      {showApiDocumentation && (
        <ApiDocumentation onClose={() => setShowApiDocumentation(false)} />
      )}
    </header>
  );
};